<template lang="pug">
tr(data-test-id="scheduled-product-row")
  td(:class="{ indent: belongsToDeal }")
    BriqMultiselect(
      v-model="bookingProductProductKey"
      :options="optionsForResource"
      track-by="Key"
      :disabled="belongsToDeal"
      display="NameInternal"
      :data-test-id="selectTestId"
      )
      template(slot="singleLabel" slot-scope="props")
        BriqMultiselectCustomLabel(
          :label="props.option.NameInternal"
          :shouldShowWarning="shouldShowWarning"
          :warningLabel="productWarningLabel"
          )
      template(slot="option" slot-scope="props")
        div {{ props.option.NameInternal }}
    .product-type {{ productType(bookingProduct.DealProductType) }}
  td
    span(v-show="!productScheduledInParallel" :data-test-id="timeTestId") {{ formattedTime }}
  td
    span.activity-label.activity-label--small(:style="resourceColor(bookingProduct.ResourceKey)") {{ resourceAbbreviation(bookingProduct.ResourceKey) }} 
    span {{ resourceName(bookingProduct.ResourceKey) }}
  td
    div(:class="bookingProductIsSplit ? 'flex' : ''")
      BookingProductQuantityPicker(
        :bookingProduct="bookingProduct"
        :disabled="belongsToDeal && !bookingProductIsSplit"
        :bookingDeal="bookingDeal"
        :belongsToDeal="belongsToDeal"
        )
      .max-quantity-of-split-deal(
        v-if="bookingProductIsSplit"
        data-test-id="max-quantity-of-split-deal"
        ) / {{ maxQuantityOfSplitDeal }}
  td
    BriqCheckbox.no-margin(:value="bookingProduct.Exclusivity === 'Booking'" :disabled="isProductExclusive || isProductResourceExclusive" @input="checked => bookingProduct.Exclusivity = (checked ? 'Booking' : 'None')")
  td.delete-col
    TrashIconButton(v-if="!belongsToDeal" @delete="$emit('delete', bookingProduct)")
</template>

<script>
import { utcToZonedTime } from 'date-fns-tz'
import TrashIconButton from '@v/bookings/edit/components/TrashIconButton.vue'
import BriqMultiselectCustomLabel from '@v/bookings/edit/UpsertBooking/BriqMultiselectCustomLabel'
import BookingProductQuantityPicker from '@v/bookings/edit/components/BookingProductQuantityPicker.vue'
import {
  pipe,
  prop,
  propOr,
  filter,
  anyPass,
  includes,
  allPass,
  compose,
  propEq,
  juxt,
} from 'ramda'
import { maybeAppendBookingProduct } from '@v/bookings/edit/util/maybeAppendBookingProduct.js'
import DealProductType from '@v/bookings/edit/models/DealProductType.js'
import { completeQuantities } from '@v/bookings/edit/util/completeBookingProductSegmentQuantities.js'
import { validateProps } from '@/vue/util/propValidators'
import BriqCheckbox from '@/vue/components-global/BriqCheckbox.vue'

export default {
  name: 'BookingProductsTableRow',
  components: {
    TrashIconButton,
    BookingProductQuantityPicker,
    BriqMultiselectCustomLabel,
    BriqCheckbox,
  },
  props: {
    bookingProduct: { type: Object, required: true },
    products: { type: Array, required: true },
    resources: { type: Object, required: true },
    bookingProductIsSplit: Boolean,
    bookingDeal: { type: Object },
    classifications: Array,
  },
  computed: {
    bookingProductProductKey: {
      get() {
        return this.bookingProduct.ProductKey
      },
      set(key) {
        this.bookingProduct.ProductKey = key
        this.bookingProduct.ProductActiveState = 'Active' // needed to remove warning styling
        // todo: make a more elegant solution in combination with the watcher in NonScheduledProductsTableRow
        const newChosenProduct = this.products.find(propEq('Key', key))
        if (newChosenProduct && this.classifications) {
          const flatSegments = this.classifications.reduce(
            (agg, next) => agg.concat(next.Segments),
            [],
          )
          this.bookingProduct.QuantitiesPerClassification = completeQuantities(
            newChosenProduct,
            flatSegments,
            this.bookingProduct,
          )
        }
      },
    },
    maxQuantityOfSplitDeal() {
      return this.bookingDeal.quantities.total
    },
    maxQuantityPerSegmentForSplitProduct() {
      return this.bookingProductIsSplit ? null : this.bookingDeal.quantities.perSegment
    },
    selectTestId() {
      return 'product-select-' + this.bookingProduct.ResourceKey
    },
    timeTestId() {
      return 'product-time-' + this.bookingProduct.Key
    },
    belongsToDeal() {
      return (
        !!this.bookingProduct.ProductGroupKey &&
        this.bookingProduct.DealProductType === DealProductType.DealProduct
      )
    },
    parentResourceKey() {
      const resource = this.resources.find(propEq('Key', this.bookingProduct.ResourceKey))
      return resource.ParentResourceKey
    },
    selectedProduct() {
      return this.products.find(p => p.Key === this.bookingProduct.ProductKey && p.Enabled)
    },
    selectedProductResource() {
      return this.selectedProduct ? this.resources[this.selectedProduct?.resourceKey] : null
    },
    isProductExclusive() {
      return this.selectedProduct?.Exclusivity === 'Booking'
    },
    isProductResourceExclusive() {
      return this.selectedProductResource?.Exclusivity === 'Booking'
    },
    productScheduledInParallel() {
      return this.selectedProduct?.scheduleInParallel
    },
    formattedTime() {
      return this.formatTime(this.bookingProduct.FromDateTimeUTC)
    },
    shouldShowWarning() {
      return (
        this.bookingProduct.ProductActiveState &&
        this.bookingProduct.ProductActiveState !== 'Active'
      )
    },
    productWarningLabel() {
      return this.shouldShowWarning ? this.bookingProduct.ProductActiveState.toLowerCase() : null
    },
    // todo: test this:
    optionsForResource() {
      return getProductOptions(this.bookingProduct)(this.products)
    },
  },
  methods: {
    formatTime(timeUtc) {
      if (!timeUtc) return '-'
      const date = new Date(timeUtc)
      const timezone = this.$timezone
      const zonedDate = utcToZonedTime(date, timezone)
      return zonedDate.toLocaleTimeString(this.$locale, { hour: 'numeric', minute: 'numeric' })
    },
    resourceAbbreviation(resourceKey) {
      if (this.resources[resourceKey] === undefined) return ''
      return this.resources[resourceKey].Abbreviation
        ? this.resources[resourceKey].Abbreviation
        : this.resources[resourceKey].Description.substring(0, 2).toUpperCase()
    },
    resourceColor(resourceKey) {
      if (this.resources[resourceKey] !== undefined && this.resources[resourceKey].Color !== null)
        return `background-color: ${this.resources[resourceKey].Color}`
      else return ''
    },
    resourceName(resourceKey) {
      if (this.resources[resourceKey] === undefined) return ''
      return this.resources[resourceKey].Description
    },
    setBookingProductAsExclusive() {
      if (this.bookingProduct.Exclusivity === 'Booking') {
        return 'Booking'
      }

      if (this.isProductExclusive || this.isProductResourceExclusive) {
        this.bookingProduct.Exclusivity = 'Booking'
      } else {
        this.bookingProduct.Exclusivity = 'None'
      }
    },
    productType(dealProductType) {
      switch (dealProductType) {
        case DealProductType.DealProduct:
          return 'Base product'
        case DealProductType.CrossSell:
          return 'Extra product'
        case DealProductType.NotSet:
          return ''
      }
    },
  },
  watch: {
    isProductExclusive() {
      this.setBookingProductAsExclusive()
    },
    isProductResourceExclusive() {
      this.setBookingProductAsExclusive()
    },
  },
  created() {
    this.setBookingProductAsExclusive()
  },
}

const sameProps = (p1, p2) => o1 => o2 => o1[p1] === o2[p2]
const productFits = pipe(
  juxt([
    sameProps('ResourceKey', 'ResourceKey'),
    compose(f => pipe(propOr([], 'childResourceKeys'), f), includes, prop('ResourceKey')),
    pipe(prop('ProductKey'), propEq('Key')),
  ]),
  anyPass,
)
const isScheduled = prop('NeedsToBeScheduled')
const isEnabled = prop('Enabled')
const filterProducts = bp => filter(allPass([productFits(bp), isScheduled, isEnabled]))
const getProductOptions = bp => pipe(filterProducts(bp), maybeAppendBookingProduct(bp))
</script>

<style scoped lang="stylus">
.no-margin
  margin 0

.indent
  padding-left 48px

.delete-col
  width 59px

.max-quantity-of-split-deal
  margin auto
  width 50%
  padding-left 10px

.product-type
  font-size 12px
  line-height 16px
  color #4E5A6A
  margin-top 6px
</style>
